import React, { useState, useMemo, useEffect, useCallback } from "react";

import { AddAssetsDialog } from "src/components/asset/add";

import { Carousel } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";
import { fetchVoicePresets } from "src/redux/actions/coaching/podcast";
import { ChatContainer } from "../chat";

import _ from "lodash";
import cx from "classnames";
import styles from "./configurations.module.scss";
import useMeasure from "react-use-measure";
import { Podcast, VoicePreset } from "src/types/coaching/podcast";
import { useSelector } from "react-redux";
import { PodcastEditState } from "../studio";

const DURATION_CHOICES = [
  {
    label: "10 mins",
    value: 10,
  },
  {
    label: "15 mins",
    value: 15,
  },
  {
    label: "20 mins",
    value: 20,
  },
  {
    label: "25 mins",
    value: 25,
  },
  {
    label: "30 mins",
    value: 30,
  },
  {
    label: "35 mins",
    value: 35,
  },
  {
    label: "40 mins",
    value: 40,
  },
  {
    label: "45 mins",
    value: 45,
  },
];

interface PodcastConfigurationsProps {
  podcast: Podcast; 
  value: PodcastEditState;
  // eslint-disable-next-line no-unused-vars
  onChange: (property: string, value: any) => void;
}

const PodcastConfigurations: React.FC<PodcastConfigurationsProps> = ({ podcast, value, onChange }) => {
  const dispatch = useDispatch();

  const [showAddAssets, setShowAddAssets] = useState(false);
  const { assets, duration, voicePreset } = value;

  useEffect(() => {
    dispatch(fetchVoicePresets());
  }, [podcast?.id]);

  const handleAddAsset = () => {
    setShowAddAssets(true);
  };

  const handleDurationChange = (duration: any) => {
    onChange("duration", duration);
  };

  const handleAddAssetFinish = useCallback((assets: any) => {
    const existingAssets = value.assets || [];
    const updatedAssets = [...existingAssets, ...assets];
    _.uniqBy(updatedAssets, "id");

    onChange("assets", updatedAssets);
  }, [value.assets, onChange]);

  const handleRemoveAsset = (assetId: string) => {
    const assets = value.assets || [];
    const updated = assets.filter((asset) => asset.id !== assetId);
    
    onChange("assets", updated);
  };

  const handleVoicePresetChange = (preset: VoicePreset) => {
    onChange("voicePreset", preset);
  }

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={cx(styles.header, styles.stickyHeader)}>
          <h4>Generate a podcast with studio</h4>
        </div>
        <div className={styles.body}>
          <div className={cx(styles.field, styles.assets)}>
            <label className={styles.label}>source &amp; link</label>
            <p className={styles.description}>
              Upload/drag and drop any assets from Paperflite library
            </p>
            <div className={styles.content}>
              <div className={cx(styles.asset, styles.add)} onClick={handleAddAsset}>
                <div className={styles.icon}>
                  <i className="paperflite-add"></i>
                </div>
                <div className={styles.name}>Add assets</div>
              </div>
              {assets.map((asset) => {
                return (
                  <div key={asset.id} className={styles.asset}>
                    <div className={styles.icon}>
                      <Thumbnail
                        className={styles.thumbnail}
                        width={120}
                        media={asset?.icon}
                        defaultImage="/images/assets/default_asset.jpg"
                      />
                      <button className={styles.remove} onClick={() => handleRemoveAsset(asset.id)}>
                        <i className="paperflite-close"></i>
                      </button>
                    </div>
                    <div className={styles.name}>
                      <Text text={asset?.name} lines={1} width={56} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={cx(styles.field, styles.duration)}>
            <label className={styles.label}>learning time</label>
            <p className={styles.description}>
              Select how much time you would like to spent learning
            </p>
            <div className={styles.content}>
              <DurationSlider
                value={duration}
                options={DURATION_CHOICES}
                onChange={handleDurationChange}
              />
            </div>
          </div>

          <VoicePresetsField value={voicePreset} onChange={handleVoicePresetChange} />

        </div>
      </div>
      <div className={styles.right}>
        <ChatContainer podcast={podcast} />
      </div>

      <AddAssetsDialog
        show={showAddAssets}
        selectableEntityTypes={["asset"]}
        source={{
          type: "course",
          entity: podcast,
        }}
        onFinish={handleAddAssetFinish}
        onClose={() => setShowAddAssets(false)}
      />
    </div>
  );
};

interface DurationSliderProps {
  options: {
    label: string;
    value: any;
  }[];
  value: any;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: any) => void;
}

const DurationSlider: React.FC<DurationSliderProps> = ({ value, onChange, options }) => {
  const [slideRef, slideBounds] = useMeasure();
  const [ticks, setTicks] = React.useState(0);
  const [valuePosition, setValuePosition] = React.useState(0);
  const [progressStyle, setProgressStyle] = React.useState({
    left: 0,
    width: 0,
  });


  const index = React.useMemo(() => {
    const index = _.findIndex(options, (option) => {
      return option.value === value;
    });
    return index === -1 ? options.length : index;
  }, [value, options]);

  const valueLabel = React.useMemo(() => {
    const index = _.findIndex(options, (option) => {
      return option.value === value;
    });
    return index === -1 ? "i'm flexible" : options[index].label;
  }, [value, index, options]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      const width = slideBounds.width - 20;
      setTicks(Math.floor(width / 6));
    }
  }, [slideBounds]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      const width = slideBounds.width - 20;
      const position = index * ((width - 20) / options.length);
      setValuePosition(position);
    }
  }, [slideBounds, index]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      console.log(index);
      const width = slideBounds.width - 20;
      const progressWidth = index * (width / options.length);
      const emptyDuration = index === options.length;

      setProgressStyle({
        left: emptyDuration ? width : 0,
        width: emptyDuration ? 20 : progressWidth,
      });
    }
  }, [slideBounds, index]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    let output = null;
    if (value >= 0 && value < options.length) {
      output = options[value].value;
    }
    onChange(output);
  }

  return (
    <div className={styles.slide}>
      <div className={styles.wrapper} ref={slideRef}>
        <div className={styles.track}>
          {_.times(ticks, (index) => (
            <div key={index} className={styles.tick} />
          ))}
          <div className={styles.end} />
        </div>
        <div className={styles.value} style={{
          left: `${valuePosition}px`,
        }}>
          <span>{valueLabel}</span>
        </div>
        <div className={styles.progress} style={progressStyle} />
        <input type="range" className={styles.slider}
          min={0} max={_.size(options)}
          value={index}
          onChange={handleSliderChange}
        />
      </div>
    </div>
  );
}


interface VoicePresetPanelProps {
  id: string;
  name: string;
}

const VoicePresetPanel: React.FC<VoicePresetPanelProps> = ({ id, name }) => {
  return (
    <div className={styles.voicePreset}>
      <div className={styles.inner}>
        <div className={styles.icon}>
          <img src="/images/coaching/podcast_host.svg" alt="Host" />
        </div>
        <button className={styles.play}>
          <i className="paperflite-play"></i>
        </button>
        <div className={styles.icon}>
          <img src="/images/coaching/podcast_host.svg" alt="Host" />
        </div>
      </div>
    </div>
  );
};

interface VoicePresetsFieldProps {
  value?: VoicePreset | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: VoicePreset) => void;
}

const VoicePresetsField: React.FC<VoicePresetsFieldProps> = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const { loading, voicePresets } = useSelector((state: any) => {
    const loading = _.get(state, "coaching.podcasts.voicePresets.loading");
    const items = _.get(state, "coaching.podcasts.voicePresets.items") || [];

    return {
      loading: loading,
      voicePresets: items,
    };
  });

  const selectedIndex = useMemo(() => {
    return voicePresets.findIndex((preset) => preset.id === value?.id);
  }, [value, voicePresets]);

  useEffect(() => {
    if (!voicePresets || voicePresets.length === 0) {
      dispatch(fetchVoicePresets());
    }
  }, [voicePresets, dispatch]);

  const handleChange = (index: number) => {
    if (index < 0 || index >= voicePresets.length) {
      return;
    }
    const selectedPreset = voicePresets[index];
    onChange(selectedPreset);
  };

  if(loading) {
    return <div></div>;
  }

  return (
    <div className={cx(styles.field, styles.voice)}>
      <label className={styles.label}>voice</label>
      <p className={styles.description}>
        Select the tone you&apos;d like your host to speak in
      </p>
      <div className={styles.content}>
        <Carousel
          activeIndex={selectedIndex}
          controls={false}
          indicators={false}
        >
          {voicePresets.map((option, index) => (
            <Carousel.Item key={index}>
              <VoicePresetPanel
                id={`mood-${index}`}
                name={option.label}
              />
            </Carousel.Item>
          ))}
        </Carousel>
        <div className={cx(styles.controls)}>
          <button
            className={cx(styles.action, styles.left)}
            onClick={() => handleChange(selectedIndex - 1)}
          >
            <i className="paperflite-dropdown"></i>
          </button>
          <span className={styles.name}>{value?.name || null}</span>
          <button
            className={cx(styles.action, styles.right)}
            onClick={() => handleChange(selectedIndex + 1)}
          >
            <i className="paperflite-dropdown"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PodcastConfigurations;
